/**
 * @file src/api/purchaseOrder.api.js
 * @description Purchase order API. Endpoints under /purchases/orders.
 * A PO has no accounting until convert-to-bill.
 */

import { apiClient } from "lib/axios";

const normalizePO = (po) => {
  if (!po) return po;
  const vendor =
    typeof po.vendorId === "object" && po.vendorId ? po.vendorId : null;
  return {
    ...po,
    id: po.id || po._id,
    vendorId: vendor ? vendor._id || vendor.id : po.vendorId,
    vendorName: po.vendorName || vendor?.name || "—",
    vendorEmail: vendor?.email || "",
    vendorPhone: vendor?.phone || "",
  };
};

const normalizeListResponse = (data) => {
  const list =
    data?.purchaseOrders || data?.data?.purchaseOrders || data?.data || [];
  return {
    ...data,
    purchaseOrders: Array.isArray(list) ? list.map(normalizePO) : [],
  };
};

const normalizeItemResponse = (data) => {
  const po =
    data?.purchaseOrder || data?.data?.purchaseOrder || data?.data || data;
  return { ...data, purchaseOrder: normalizePO(po) };
};

const buildCleanPayload = (payload) => ({
  vendorId: payload.vendorId,
  branchId: payload.branchId || null,
  issueDate: payload.issueDate,
  expectedDeliveryDate: payload.expectedDeliveryDate || null,
  currency: payload.currency || "SAR",
  notes: payload.notes || "",
  lineItems: Array.isArray(payload.lineItems) ? payload.lineItems : [],
});

export const getPurchaseOrders = async (params = {}) => {
  const { data } = await apiClient.get("/purchases/orders", { params });
  return normalizeListResponse(data);
};

export const getPurchaseOrderById = async (id) => {
  const { data } = await apiClient.get(`/purchases/orders/${id}`);
  return normalizeItemResponse(data);
};

export const createPurchaseOrder = async (payload) => {
  const { data } = await apiClient.post(
    "/purchases/orders",
    buildCleanPayload(payload),
  );
  return normalizeItemResponse(data);
};

export const updatePurchaseOrder = async (id, payload) => {
  const { data } = await apiClient.patch(`/purchases/orders/${id}`, payload);
  return normalizeItemResponse(data);
};

export const submitPurchaseOrder = async (id) => {
  const { data } = await apiClient.post(`/purchases/orders/${id}/submit`, {});
  return normalizeItemResponse(data);
};

export const approvePurchaseOrder = async (id) => {
  const { data } = await apiClient.post(`/purchases/orders/${id}/approve`, {});
  return normalizeItemResponse(data);
};

export const cancelPurchaseOrder = async (id) => {
  const { data } = await apiClient.post(`/purchases/orders/${id}/cancel`, {});
  return normalizeItemResponse(data);
};

export const convertPurchaseOrderToBill = async (id, body = {}) => {
  const { data } = await apiClient.post(
    `/purchases/orders/${id}/convert`,
    body,
  );
  return data; // { success, bill }
};

export const deletePurchaseOrder = async (id) => {
  const { data } = await apiClient.delete(`/purchases/orders/${id}`);
  return data;
};

// ── PDF (fetched as an authenticated blob, then opened/downloaded client-side) ──

export const fetchPurchaseOrderPdfBlob = async (id) => {
  const { data } = await apiClient.get(`/purchases/orders/${id}/pdf`, {
    responseType: "blob",
  });
  return data;
};

export const viewPurchaseOrderPdf = async (id) => {
  const blob = await fetchPurchaseOrderPdfBlob(id);
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const downloadPurchaseOrderPdf = async (id, filename) => {
  const blob = await fetchPurchaseOrderPdfBlob(id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `purchase-order-${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
